import React, { useEffect, useState } from "react"
import { Tabs } from "antd"
import SuperadminDisplay from "features/Superadmin/components/SuperadminDisplay.component"
import AiMetricsDisplay from "features/Superadmin/components/AiMetricsDisplay.component"
import { useSuperadmin } from "features/Superadmin/api/useSuperadmin"
import handleSequenceError from "utils/handleSequenceError"
import { notify } from "shared/ui/Notifications/notify"
import {
  getServerFromHostname,
  otherServers,
  ServerUnit,
} from "utils/server"

/**
 * SuperadminContainer component is responsible for fetching the units from the sequence
 * and rendering the SuperadminDisplay component. It handles API calls and manages the state
 * related to the units and API loading indicators.
 *
 * @component
 * @returns The rendered SuperadminContainer component.
 */
const SuperadminContainer: React.FC = () => {
  const {
    getSequenceUnits,
    isLoadingSequenceUnits,
    getUnitsForServer,
    makeAdminApi,
    isCallingAdminApi,
    getAiUsageMetrics,
    isLoadingAiMetrics,
  } = useSuperadmin()

  const hostServer = getServerFromHostname()
  const [units, setUnits] = useState<ServerUnit[]>([])

  /**
   * Fetches the units of this server (same-origin) and, on a server host,
   * also every other server's units (cross-origin). Each other server is
   * fetched independently and degrades silently when unreachable (e.g. not
   * deployed yet, or CORS not configured), so one missing server doesn't hide
   * the rest.
   */
  const fetchUnits = async (): Promise<void> => {
    let localUnits: ServerUnit[] = []

    try {
      const res = await getSequenceUnits()

      if (
        res.data &&
        handleSequenceError(res.data, "Error Retrieving Units!")
      ) {
        localUnits = res.data
          .split("\n")
          .filter((unit) => unit.trim() !== "")
          .map((name) => ({ name, serverId: hostServer }))
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Error Fetching Units!", errorMessage)
    }

    setUnits(localUnits)

    if (!hostServer) return

    const results = await Promise.allSettled(
      otherServers(hostServer).map(async (server) => ({
        server,
        units: await getUnitsForServer(server),
      }))
    )

    const otherUnits: ServerUnit[] = results.flatMap((result) =>
      result.status === "fulfilled"
        ? result.value.units.map((name) => ({
            name,
            serverId: result.value.server,
          }))
        : []
    )

    results
      .filter((result) => result.status === "rejected")
      .forEach((result) =>
        // Server not reachable (not deployed yet, or CORS not configured).
        console.warn(
          "Other-server units unavailable:",
          (result as PromiseRejectedResult).reason
        )
      )

    setUnits([...localUnits, ...otherUnits])
  }

  // Fetch units on component mount
  useEffect(() => {
    fetchUnits()
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tabItems = [
    {
      key: "admin",
      label: "Admin Controls",
      children: (
        <SuperadminDisplay
          isLoading={isLoadingSequenceUnits}
          units={units}
          makeAdminApi={makeAdminApi}
          isCallingAdminApi={isCallingAdminApi}
        />
      ),
    },
    {
      key: "ai-metrics",
      label: "AI Metrics",
      children: (
        <AiMetricsDisplay
          getAiUsageMetrics={getAiUsageMetrics}
          isLoadingAiMetrics={isLoadingAiMetrics}
          units={units.map((unit) => unit.name)}
        />
      ),
    },
  ]

  return (
    <div id="superadmin" className="flex flex-col p-5">
      <Tabs
        size="large"
        type="card"
        defaultActiveKey="admin"
        items={tabItems}
        className="project-details-tabs bg-white rounded-2xl h-full"
      />
    </div>
  )
}

export default SuperadminContainer
